'use client';

import { useEffect, useRef, useState } from 'react';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import PageBanner from './PageBanner';
import { RECITERS, SURAH_IDS, getArabic, getAudioUrl, getSurahMeta } from '@/lib/quran-data';
import { Play, Pause, Square, SkipForward, AudioLines } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function ListenSection() {
  const { uiLang, surah, setSurah, reciter, setReciter, nowPlaying, setNowPlaying, markListened, markRead } = useApp();
  const t = DICTS[uiLang];
  const [playingAyah, setPlayingAyah] = useState<number | null>(null);
  const [queue, setQueue] = useState<number[] | null>(null); // full-surah sequential playback
  const [loading, setLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const arabic = getArabic(surah);
  const meta = getSurahMeta(surah);
  const isThisSurah = nowPlaying?.surah === surah;

  useEffect(() => {
    return () => { audioRef.current?.pause(); audioRef.current = null; };
  }, []);

  const playFrom = (startIndex: number, fullSurah: boolean) => {
    if (!audioRef.current) audioRef.current = new Audio();
    const el = audioRef.current;
    el.pause();
    setQueue(fullSurah ? [startIndex] : null);

    const playIndex = (i: number) => {
      if (i >= arabic.length) { setPlayingAyah(null); setNowPlaying(null); setQueue(null); return; }
      const ayah = arabic[i];
      el.src = getAudioUrl(useApp.getState().reciter, ayah.g);
      setPlayingAyah(ayah.n);
      setNowPlaying({ surah, ayah: ayah.n });
      markListened(surah, ayah.n);
      markRead(surah, ayah.n);
      setLoading(true);
      el.play().catch(() => setLoading(false));
      el.onended = () => {
        if (useApp.getState().nowPlaying?.ayah === ayah.n) playIndex(i + 1);
      };
    };
    playIndex(startIndex);
  };

  const stopAll = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingAyah(null);
    setNowPlaying(null);
    setQueue(null);
  };

  const toggleAyah = (idx: number) => {
    if (playingAyah === arabic[idx].n && isThisSurah) { stopAll(); return; }
    playFrom(idx, false);
  };

  // Auto-scroll to playing verse
  useEffect(() => {
    if (playingAyah && isThisSurah && listRef.current) {
      listRef.current.querySelector(`[data-ayah="${playingAyah}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [playingAyah, isThisSurah]);

  return (
    <>
      <PageBanner
        src="/images/banners/listen_banner.png"
        eyebrow="LISTEN — التلاوة"
        height="sm"
        title={t.listen.title}
        sub={t.listen.subtitle}
      />
      <section id="section-listen" className="mx-auto max-w-4xl scroll-mt-20 px-4 py-14">

      {/* Controls */}
      <div className="gold-ornate-card mb-6 flex flex-col gap-3 rounded-xl p-4 sm:flex-row sm:items-end">
        <label className="flex flex-1 flex-col gap-1.5">
          <span className="text-[11px] font-semibold tracking-wider text-[#d4af37] uppercase">{t.read.select_surah}</span>
          <select
            value={surah}
            onChange={(e) => { stopAll(); setSurah(Number(e.target.value)); }}
            className="rounded-md border border-[rgba(212,175,55,0.35)] bg-[#032019] px-3 py-2.5 text-sm text-[#f5f0e1] outline-none focus:ring-2 focus:ring-[#d4af37]/50"
          >
            {SURAH_IDS.map((s) => (
              <option key={s} value={s}>{s}. {getSurahMeta(s).nameEn}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-1 flex-col gap-1.5">
          <span className="text-[11px] font-semibold tracking-wider text-[#d4af37] uppercase">{t.listen.select_reciter}</span>
          <select
            value={reciter}
            onChange={(e) => { setReciter(e.target.value); }}
            className="rounded-md border border-[rgba(212,175,55,0.35)] bg-[#032019] px-3 py-2.5 text-sm text-[#f5f0e1] outline-none focus:ring-2 focus:ring-[#d4af37]/50"
          >
            {RECITERS.map((r) => (
              <option key={r.id} value={r.id}>{t.listen.reciters[r.key as keyof typeof t.listen.reciters]}</option>
            ))}
          </select>
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => playFrom(0, true)}
            disabled={!!playingAyah}
            className="flex flex-1 items-center justify-center gap-2 rounded-md bg-gradient-to-b from-[#e8c86a] to-[#b8933a] px-4 py-2.5 text-sm font-bold text-[#032019] transition-transform hover:scale-[1.02] disabled:opacity-40 sm:flex-none"
          >
            <Play className="h-4 w-4 rtl-flip" /> {t.read.play_all}
          </button>
          {playingAyah && (
            <button
              onClick={stopAll}
              className="flex items-center justify-center gap-2 rounded-md border border-[#e05252]/50 bg-[#e05252]/15 px-4 py-2.5 text-sm font-bold text-[#ff9b9b]"
            >
              <Square className="h-4 w-4" /> {t.read.stop}
            </button>
          )}
        </div>
      </div>

      {/* Now playing banner */}
      <div className="mb-6 flex items-center gap-3 rounded-xl border border-[rgba(212,175,55,0.3)] bg-[rgba(212,175,55,0.06)] px-4 py-3">
        <AudioLines className={cn('h-5 w-5 text-[#d4af37]', playingAyah && 'animate-pulse')} aria-hidden />
        <div className="flex-1 text-sm">
          {playingAyah && isThisSurah ? (
            <span className="text-[#f0d98c]">
              {t.listen.now_playing}: <strong>{meta.nameEn}</strong> — {t.listen.verse} {playingAyah} / {arabic.length}
              {loading && <span className="ml-2 text-xs text-[#cfe0cf]/60">{t.read.loading_audio}</span>}
            </span>
          ) : playingAyah ? (
            <span className="text-[#f0d98c]">{t.listen.now_playing}: {getSurahMeta(nowPlaying!.surah).nameEn} — {t.listen.verse} {nowPlaying!.ayah}</span>
          ) : (
            <span className="text-[#cfe0cf]/60">{t.listen.nothing_playing}</span>
          )}
        </div>
        {queue && playingAyah && (
          <button
            onClick={stopAll}
            className="flex items-center gap-1 rounded-md border border-[rgba(212,175,55,0.4)] px-3 py-1.5 text-xs text-[#f0d98c]"
          >
            <SkipForward className="h-3 w-3 rtl-flip" /> {t.read.verses} {arabic.length}
          </button>
        )}
      </div>

      {/* Verse chips */}
      <div ref={listRef} className="gold-scroll flex max-h-72 flex-wrap gap-2 overflow-y-auto rounded-xl border border-[rgba(212,175,55,0.2)] bg-[rgba(3,32,25,0.5)] p-4">
        {arabic.map((a, idx) => (
          <button
            key={a.n}
            data-ayah={a.n}
            onClick={() => toggleAyah(idx)}
            className={cn(
              'flex h-10 min-w-10 items-center justify-center rounded-lg border px-2 text-sm font-bold transition-all',
              playingAyah === a.n && isThisSurah
                ? 'border-[#d4af37] bg-[#d4af37] text-[#032019] shadow-[0_0_16px_rgba(212,175,55,0.4)]'
                : 'border-[rgba(212,175,55,0.3)] text-[#e8dcc0] hover:border-[rgba(212,175,55,0.7)] hover:bg-[rgba(212,175,55,0.1)]'
            )}
            aria-label={`${t.listen.verse} ${a.n}`}
          >
            {playingAyah === a.n && isThisSurah ? <Pause className="h-4 w-4" /> : a.n}
          </button>
        ))}
      </div>

      <p className="mt-3 text-center text-xs text-[#cfe0cf]/50">
        {meta.nameEn} — {uiLang === 'ar' ? meta.nameAr : `${meta.ayahs} ${t.read.verses}`} · {reciter.replace('ar.', '').replace('murattal', '').replace('mujawwad', '')}
      </p>
      </section>
    </>
  );
}
